/**
 * Schema validation wrapper using TypeBox's native value checking.
 *
 * Uses TypeBox's interpreter-based validation (no eval/Function constructor)
 * which is CSP-safe. Provides the bridge between TypeBox schemas and the
 * existing ValidationResult interface, so storage services can swap in
 * schema-based validation without changing their error-handling code.
 */

import { Check, Errors } from 'typebox/value'
import { Set as SetFormat } from 'typebox/format'
import type { TSchema } from 'typebox'
import type { ValidationResult, ValidationError } from '../storage/validation.js'

// Register the date-time format — must be a full ISO 8601 date-time (not date-only)
SetFormat(
  'date-time',
  value => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) && !isNaN(Date.parse(value))
)

/**
 * Validate data against a TypeBox schema.
 *
 * Returns a ValidationResult compatible with the existing storage layer so
 * callers don't need to change their error handling.
 */
export function validateSchema(schema: TSchema, data: unknown): ValidationResult {
  if (Check(schema, data)) {
    return { isValid: true, errors: [] }
  }

  const errors: ValidationError[] = [...Errors(schema, data)].map(err => ({
    field:
      (err.instancePath.replace(/^\//, '').replace(/\//g, '.') ||
        (err.keyword === 'required'
          ? (err.params as { requiredProperties?: string[] }).requiredProperties?.[0]
          : undefined)) ??
      'unknown',
    message: err.message,
    code: err.keyword.toUpperCase(),
    value: undefined,
  }))

  return { isValid: false, errors }
}
